import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import {
    createRootRouteWithContext,
    HeadContent,
    Scripts,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";

import { CmsHeadScripts } from "#/_packages/umbrella/features/routing/components/cms-head-scripts.tsx";
import { getPublicEnv } from "#/_packages/umbrella/config/utils.ts";
import TanStackQueryDevtools from "#/_packages/umbrella/integrations/tanstack-query/devtools";
import type { UmbracoBlockPreviewBlock } from "#/app/routes/_umbraco/_preview/umbraco/block-preview.tsx";
import { getLocale } from "#/paraglide/runtime";

if (
    import.meta.env.DEV &&
    !import.meta.env.SSR &&
    import.meta.env.VITE_CLICK_SOURCE_ENABLED === "true"
) {
    void import("click-source-extension/runtime")
        .then(({ initializeClickSource }) => initializeClickSource())
        .catch((error: unknown) =>
            console.error(
                "[click-source] Failed to initialize the browser overlay.",
                error,
            ),
        );
}

interface UmbrellaRouterContext {
    queryClient: QueryClient;
    previewBlock?: UmbracoBlockPreviewBlock;
}

export const Route = createRootRouteWithContext<UmbrellaRouterContext>()({
    beforeLoad: async () => {
        // Other redirect strategies are possible; see
        // https://github.com/TanStack/router/tree/main/examples/react/i18n-paraglide#offline-redirect
        if (typeof document !== "undefined") {
            document.documentElement.setAttribute("lang", getLocale());
        }
    },
    head: () => ({
        meta: [
            {
                name: "viewport",
                content: "width=device-width, initial-scale=1",
            },
        ],
    }),
    shellComponent: RootDocument,
    scripts: async () => {
        const env = await getPublicEnv();

        return [
            {
                children: `window.env = ${JSON.stringify(env)};`,
            },
        ];
    },
});

function RootDocument({ children }: { children: React.ReactNode }) {
    return (
        <html lang={getLocale()}>
            <head>
                <HeadContent />
                <CmsHeadScripts />
            </head>
            <body>
                {children}

                <TanStackDevtools
                    config={{
                        position: "bottom-right",
                    }}
                    plugins={[
                        {
                            name: "Tanstack Router",
                            render: <TanStackRouterDevtoolsPanel />,
                        },
                        TanStackQueryDevtools,
                    ]}
                />
                <Scripts />
            </body>
        </html>
    );
}
